import React, { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import classNames from 'classnames';
import { ClipLoader } from 'react-spinners';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  isLoading?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, title, children, isLoading = false }) => {
  const { mode } = useSelector((state: RootState) => state.theme);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Sidebar Panel */}
          <motion.div 
            className={classNames(
              "fixed right-0 top-0 h-full w-[40%] min-w-[320px] max-w-[600px] z-50 shadow-xl overflow-hidden",
              {
                "bg-white": mode === 'light',
                "bg-gray-900": mode === 'dark'
              }
            )}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 20, stiffness: 250 }}
          >
            {/* Header */}
            <div className={classNames(
              "flex items-center justify-between p-4 border-b",
              {
                "border-gray-200": mode === 'light',
                "border-gray-700": mode === 'dark'
              }
            )}>
              <h2 className={classNames(
                "text-lg font-semibold",
                {
                  "text-gray-800": mode === 'light',
                  "text-gray-100": mode === 'dark'
                }
              )}>
                {title}
              </h2>
              <button 
                onClick={onClose}
                className={classNames(
                  "p-2 rounded-full transition-colors",
                  {
                    "text-gray-500 hover:bg-gray-100": mode === 'light',
                    "text-gray-400 hover:bg-gray-800": mode === 'dark'
                  }
                )}
              >
                <FiX size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div className="relative p-4 h-[calc(100%-4rem)] overflow-y-auto">
              {/* Loading overlay */}
              {isLoading && (
                <div
                  className={classNames(
                    "absolute inset-0 flex flex-col items-center justify-center z-10",
                    {
                      "bg-white/90": mode === 'light',
                      "bg-gray-900/90": mode === 'dark'
                    }
                  )}
                  role="status"
                  aria-live="polite"
                >
                  <ClipLoader size={28} color={mode === 'dark' ? '#60A5FA' : '#3B82F6'} />
                  <p
                    className={classNames("mt-3 text-sm", {
                      "text-gray-700": mode === 'light',
                      "text-gray-200": mode === 'dark'
                    })}
                  >
                    Loading, please wait…
                  </p>
                </div>
              )}
              <div className={classNames({
                'opacity-50 pointer-events-none': isLoading
              })} aria-busy={isLoading}>
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;